import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { TableActionButton } from '../../components/ui/TableActionButton';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { AppUserManagement, listUsers } from '../../services/accessControlApi';
import {
  AccessControlTeamMember,
  addTeamMember,
  listTeamMembers,
  listTeams,
  removeTeamMember,
  updateTeamMember,
} from '../../services/teamsApi';

export function TeamMembersPage() {
  const { teamId = '' } = useParams();
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('access-control.teams');
  const [teamName, setTeamName] = useState('');
  const [members, setMembers] = useState<AccessControlTeamMember[]>([]);
  const [users, setUsers] = useState<AppUserManagement[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedBossUserId, setSelectedBossUserId] = useState('');
  const [isBoss, setIsBoss] = useState(false);

  const availableUsers = useMemo(() => {
    const memberIds = new Set(members.map((member) => member.userId));
    return users.filter((user) => user.isActive && !memberIds.has(user.id));
  }, [members, users]);

  const bossOptions = useMemo(
    () => members.filter((member) => member.isLeader || member.userId !== selectedUserId),
    [members, selectedUserId],
  );

  async function loadData(): Promise<void> {
    setLoading(true);
    try {
      const [teamsResult, membersResult, usersResult] = await Promise.all([
        listTeams({ page: 1, pageSize: 200 }),
        listTeamMembers(teamId),
        listUsers({ page: 1, pageSize: 200 }),
      ]);

      setTeamName(teamsResult.items.find((team) => team.id === teamId)?.name ?? '');
      setMembers(membersResult);
      setUsers(usersResult.items);
      if (!selectedUserId) {
        const firstAvailable = usersResult.items.find(
          (user) => user.isActive && !membersResult.some((member) => member.userId === user.id),
        );
        setSelectedUserId(firstAvailable?.id ?? '');
      }
    } catch {
      toast.error('Falha ao carregar membros da equipe.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  async function handleAddMember(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite || !selectedUserId) return;

    try {
      await addTeamMember(teamId, {
        userId: selectedUserId,
        isLeader: isBoss,
        bossUserId: selectedBossUserId || null,
      });
      toast.success('Membro adicionado.');
      setIsBoss(false);
      setSelectedBossUserId('');
      await loadData();
    } catch {
      toast.error('Falha ao adicionar membro.');
    }
  }

  async function handleToggleBoss(member: AccessControlTeamMember): Promise<void> {
    if (!canWrite) return;

    try {
      await updateTeamMember(teamId, member.userId, {
        userId: member.userId,
        isLeader: !member.isLeader,
        bossUserId: member.bossUserId ?? null,
      });
      toast.success(member.isLeader ? 'Removido da chefia.' : 'Definido como chefe da equipe.');
      await loadData();
    } catch {
      toast.error('Falha ao atualizar membro.');
    }
  }

  async function handleBossChange(member: AccessControlTeamMember, bossUserId: string): Promise<void> {
    if (!canWrite) return;

    try {
      await updateTeamMember(teamId, member.userId, {
        userId: member.userId,
        isLeader: member.isLeader,
        bossUserId: bossUserId || null,
      });
      toast.success('Chefe direto atualizado.');
      await loadData();
    } catch {
      toast.error('Falha ao atualizar chefe direto.');
    }
  }

  async function handleRemoveMember(member: AccessControlTeamMember): Promise<void> {
    if (!canWrite) return;
    if (
      !(await confirm({
        title: 'Remover membro',
        message: `Remover ${member.userName} da equipe?`,
        confirmLabel: 'Remover',
        tone: 'danger',
      }))
    ) {
      return;
    }

    try {
      await removeTeamMember(teamId, member.userId);
      toast.success('Membro removido.');
      await loadData();
    } catch {
      toast.error('Falha ao remover membro.');
    }
  }

  if (loading) return <p className="erp-loading">Carregando...</p>;

  return (
    <PageShell>
      <div>
        <Link to="/admin/teams" className="erp-link-back">
          ← Voltar para equipes
        </Link>
        <PageHeader
          title={`Membros — ${teamName}`}
          description="Defina chefes da equipe e o chefe direto de cada membro. Chefes recebem alertas sobre tarefas próprias e de subordinados."
        />
      </div>

      {canWrite ? (
        <form onSubmit={(event) => void handleAddMember(event)} className="erp-card">
          <div className="erp-card-body space-y-3">
            <h2 className="font-semibold">Adicionar membro</h2>
            <div className="erp-form-grid md:grid-cols-3">
              <select
                className="erp-input-compact"
                value={selectedUserId}
                onChange={(event) => setSelectedUserId(event.target.value)}
                required
              >
                <option value="">Selecione um usuário</option>
                {availableUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
              <select
                className="erp-input-compact"
                value={selectedBossUserId}
                onChange={(event) => setSelectedBossUserId(event.target.value)}
              >
                <option value="">Chefe direto (opcional)</option>
                {bossOptions.map((member) => (
                  <option key={member.userId} value={member.userId}>
                    {member.userName}
                  </option>
                ))}
              </select>
              <label className="erp-label-inline">
                <input
                  type="checkbox"
                  checked={isBoss}
                  onChange={(event) => setIsBoss(event.target.checked)}
                />
                Chefe da equipe
              </label>
            </div>
            <div className="erp-btn-actions">
              <button type="submit" className="erp-btn-primary" disabled={availableUsers.length === 0}>
                Adicionar
              </button>
            </div>
          </div>
        </form>
      ) : null}

      <div className="erp-table-wrap">
        <div className="erp-table-scroll">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Chefe da equipe</th>
                <th>Chefe direto</th>
                {canWrite ? <th>Ações</th> : null}
              </tr>
            </thead>
            <tbody>
              {members.length === 0 ? (
                <tr>
                  <td colSpan={canWrite ? 5 : 4} className="text-secondary-600">
                    Nenhum membro nesta equipe.
                  </td>
                </tr>
              ) : (
                members.map((member) => (
                  <tr key={member.id}>
                    <td>{member.userName}</td>
                    <td>{member.userEmail}</td>
                    <td>{member.isLeader ? 'Sim' : 'Não'}</td>
                    <td>
                      {canWrite ? (
                        <select
                          className="erp-input-compact"
                          value={member.bossUserId ?? ''}
                          onChange={(event) => void handleBossChange(member, event.target.value)}
                        >
                          <option value="">—</option>
                          {members
                            .filter((option) => option.userId !== member.userId)
                            .map((option) => (
                              <option key={option.userId} value={option.userId}>
                                {option.userName}
                              </option>
                            ))}
                        </select>
                      ) : (
                        (member.bossUserName ?? '—')
                      )}
                    </td>
                    {canWrite ? (
                      <td>
                        <div className="flex items-center gap-1">
                          <TableActionButton onClick={() => void handleToggleBoss(member)}>
                            {member.isLeader ? 'Remover chefia' : 'Tornar chefe'}
                          </TableActionButton>
                          <DeleteIconButton
                            label="Remover membro"
                            onClick={() => void handleRemoveMember(member)}
                          />
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </PageShell>
  );
}
